import { ErrorCode, type ProblemDetails } from '@sahab/contracts'
import { readSse } from './sse'

/** Problem codes the client produces itself, when no problem document came back. */
export const ClientErrorCode = {
  /** fetch() failed: offline, DNS, connection refused. */
  NETWORK_ERROR: 'NETWORK_ERROR',
  /** A gateway/proxy answered instead of the API (non-problem 5xx). */
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const

export interface FieldIssue {
  path: string
  message: string
}

export interface ApiErrorInit {
  status: number
  code: string
  detail?: string | undefined
  errors?: FieldIssue[] | undefined
  requestId?: string | undefined
  /** The problem document's other members (a rule's details, e.g. INSUFFICIENT_STOCK's shortages). */
  extensions?: Readonly<Record<string, unknown>> | undefined
  /** Seconds from the `Retry-After` header (a 409 while a document is rendering, a 429). */
  retryAfter?: number | undefined
}

/** Every failed API call rejects with this; `code` is the stable, machine-readable part. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string | undefined
  readonly errors: FieldIssue[]
  readonly requestId: string | undefined
  /** Members of the problem document beyond the standard ones (RFC 9457 extension members). */
  readonly extensions: Readonly<Record<string, unknown>>
  /** Seconds the server asked the client to wait before trying again (`Retry-After`), if it said. */
  readonly retryAfter: number | undefined

  constructor(init: ApiErrorInit) {
    super(`${init.status} ${init.code}${init.detail ? `: ${init.detail}` : ''}`)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.detail = init.detail
    this.errors = init.errors ?? []
    this.requestId = init.requestId
    this.extensions = init.extensions ?? {}
    this.retryAfter = init.retryAfter
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

const FALLBACK_CODES: Record<number, string> = {
  400: ErrorCode.VALIDATION_FAILED,
  401: ErrorCode.UNAUTHENTICATED,
  403: ErrorCode.FORBIDDEN,
  404: ErrorCode.NOT_FOUND,
  409: ErrorCode.CONFLICT,
  422: ErrorCode.VALIDATION_FAILED,
  429: ErrorCode.RATE_LIMITED,
}

/** The members every problem document may carry; anything else is a rule's own detail. */
const STANDARD_MEMBERS = new Set([
  'type',
  'title',
  'status',
  'detail',
  'instance',
  'code',
  'errors',
  'requestId',
])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseIssues(value: unknown): FieldIssue[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) =>
    isRecord(item) && typeof item.path === 'string' && typeof item.message === 'string'
      ? [{ path: item.path, message: item.message }]
      : [],
  )
}

/**
 * Turns a non-2xx response into an ApiError. RFC 9457 bodies (application/problem+json) are trusted
 * for code/detail/errors; anything else (a proxy's HTML error page, an empty body) gets a code
 * derived from the status.
 */
export async function parseErrorResponse(res: Response): Promise<ApiError> {
  const headerRequestId = res.headers.get('x-request-id') ?? undefined
  const retryAfter = retryAfterSeconds(res.headers.get('retry-after'))
  const contentType = res.headers.get('content-type') ?? ''
  let body: unknown
  if (/application\/(problem\+)?json/i.test(contentType)) {
    try {
      body = await res.json()
    } catch {
      body = undefined
    }
  }

  if (isRecord(body) && typeof body.code === 'string') {
    const problem = body as Partial<ProblemDetails>
    const extensions = Object.fromEntries(Object.entries(body).filter(([key]) => !STANDARD_MEMBERS.has(key)))
    return new ApiError({
      status: res.status,
      code: body.code,
      detail: typeof problem.detail === 'string' ? problem.detail : undefined,
      errors: parseIssues(body.errors),
      requestId: typeof problem.requestId === 'string' ? problem.requestId : headerRequestId,
      extensions,
      retryAfter,
    })
  }

  const code =
    FALLBACK_CODES[res.status] ??
    (res.status >= 500 ? ClientErrorCode.SERVICE_UNAVAILABLE : ErrorCode.INTERNAL)
  return new ApiError({ status: res.status, code, requestId: headerRequestId, retryAfter })
}

/** `Retry-After: 2` → 2. The HTTP-date form is not used by the API, so it reads as unknown. */
function retryAfterSeconds(header: string | null): number | undefined {
  if (!header || !/^\d{1,6}$/.test(header.trim())) return undefined
  return Number(header.trim())
}

type UnauthorizedHandler = () => void
let unauthorizedHandler: UnauthorizedHandler | null = null

/**
 * Registered once by the app: clears the session and sends the user to /login (keeping where they
 * were). Kept as a hook so this module has no dependency on the router or the query client.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler
}

/** Problem code the API answers every request with while the user's password must be changed. */
export const PASSWORD_CHANGE_REQUIRED = 'PASSWORD_CHANGE_REQUIRED'

let passwordChangeHandler: UnauthorizedHandler | null = null

/**
 * Registered once by the app: any 403 PASSWORD_CHANGE_REQUIRED (an administrator reset the
 * password while this tab was open) sends the user to the change-password page.
 */
export function setPasswordChangeRequiredHandler(handler: UnauthorizedHandler | null): void {
  passwordChangeHandler = handler
}

export type QueryValue = string | number | boolean | null | undefined

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, QueryValue>
  signal?: AbortSignal | undefined
  /** For the sign-in and session probe calls, where a 401 is an expected answer, not an expiry. */
  skipAuthRedirect?: boolean
}

export const API_BASE = '/api/v1'

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return `${API_BASE}${path}${qs ? `?${qs}` : ''}`
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, skipAuthRedirect = false } = options
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let res: Response
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: signal ?? null,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError({ status: 0, code: ClientErrorCode.NETWORK_ERROR })
  }

  if (!res.ok) {
    const error = await parseErrorResponse(res)
    if (error.status === 401 && !skipAuthRedirect) unauthorizedHandler?.()
    if (error.status === 403 && error.code === PASSWORD_CHANGE_REQUIRED) passwordChangeHandler?.()
    throw error
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') return undefined as T
  return (await res.json()) as T
}

type BodylessOptions = Omit<RequestOptions, 'method' | 'body'>

/** A file the API sent back (an export): its content and the name the server suggested. */
export interface DownloadedFile {
  blob: Blob
  filename: string
}

/** `attachment; filename="customers.csv"; filename*=UTF-8''…` → the (decoded) file name. */
export function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header)
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim())
    } catch {
      // Malformed escape: fall through to the plain parameter.
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header)
  return plain?.[1]?.trim() || fallback
}

/**
 * A GET that answers with a file (CSV exports, attachments, the logo): the same session, errors and
 * redirects as every other call, but the body comes back as a Blob. The caller saves it (`saveFile`)
 * or shows it from an object URL.
 */
export async function apiDownload(
  path: string,
  options: BodylessOptions & { fallbackName?: string; accept?: string } = {},
): Promise<DownloadedFile> {
  const { query, signal, fallbackName = 'download', skipAuthRedirect = false } = options
  const accept = options.accept ?? 'text/csv, application/octet-stream, application/problem+json'
  let res: Response
  try {
    res = await fetch(buildUrl(path, query), {
      method: 'GET',
      headers: { Accept: accept },
      credentials: 'same-origin',
      signal: signal ?? null,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError({ status: 0, code: ClientErrorCode.NETWORK_ERROR })
  }
  if (!res.ok) {
    const error = await parseErrorResponse(res)
    if (error.status === 401 && !skipAuthRedirect) unauthorizedHandler?.()
    if (error.status === 403 && error.code === PASSWORD_CHANGE_REQUIRED) passwordChangeHandler?.()
    throw error
  }
  return {
    blob: await res.blob(),
    filename: filenameFromDisposition(res.headers.get('content-disposition'), fallbackName),
  }
}

// ── streams ──────────────────────────────────────────────────────────────────

export interface StreamOptions {
  query?: Record<string, QueryValue>
  signal?: AbortSignal | undefined
}

/**
 * A POST answered with `text/event-stream` (the assistant's runs): the same session, problem
 * documents and redirects as every other call. Every refusal before the stream starts is a problem
 * document, thrown as the ApiError `apiRequest` would throw. Then each event's `data` is yielded as
 * parsed JSON, as it arrives; `:` comments (the server's keep-alive pings) are skipped. Aborting
 * `signal` stops reading and rejects with an AbortError. A `FormData` body goes as `multipart/form-data`
 * (a message with documents), the browser setting the boundary.
 */
export async function* apiStream(
  path: string,
  body: unknown,
  options: StreamOptions = {},
): AsyncGenerator<unknown> {
  const { query, signal } = options
  const multipart = body instanceof FormData
  let res: Response
  try {
    res = await fetch(buildUrl(path, query), {
      method: 'POST',
      headers: multipart
        ? { Accept: 'text/event-stream' }
        : { Accept: 'text/event-stream', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: multipart ? body : JSON.stringify(body ?? {}),
      signal: signal ?? null,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError({ status: 0, code: ClientErrorCode.NETWORK_ERROR })
  }
  if (!res.ok) {
    const error = await parseErrorResponse(res)
    if (error.status === 401) unauthorizedHandler?.()
    if (error.status === 403 && error.code === PASSWORD_CHANGE_REQUIRED) passwordChangeHandler?.()
    throw error
  }
  if (!res.body) return
  try {
    for await (const message of readSse(res.body, signal)) {
      try {
        yield JSON.parse(message.data) as unknown
      } catch (error) {
        if (error instanceof SyntaxError) continue
        throw error
      }
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    if (error instanceof ApiError) throw error
    // The connection dropped mid-stream.
    throw new ApiError({ status: 0, code: ClientErrorCode.NETWORK_ERROR })
  }
}

// ── uploads ──────────────────────────────────────────────────────────────────

/** One multipart upload, as a transport sends it. */
export interface UploadRequest {
  url: string
  method: 'POST' | 'PUT'
  body: FormData
  signal?: AbortSignal | undefined
  /** Bytes sent so far of the whole request body (the multipart envelope included). */
  onProgress?: ((sent: number, total: number) => void) | undefined
}

/** Sends an upload and resolves with the server's answer, whatever its status. */
export type UploadTransport = (request: UploadRequest) => Promise<Response>

function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError')
}

/**
 * `XMLHttpRequest`, for its upload progress events (fetch has none for a request body). The answer
 * is rebuilt as a `Response`, so errors are parsed exactly as every other call's.
 */
export const xhrUploadTransport: UploadTransport = ({ url, method, body, signal, onProgress }) =>
  new Promise<Response>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    const xhr = new XMLHttpRequest()
    xhr.open(method, url)
    xhr.withCredentials = true
    // The answer is JSON (the file's record) or a problem document: text is all it needs.
    xhr.responseType = 'text'
    xhr.setRequestHeader('Accept', 'application/json')
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total)
    }
    const onAbort = () => xhr.abort()
    signal?.addEventListener('abort', onAbort, { once: true })
    const done = () => signal?.removeEventListener('abort', onAbort)
    xhr.onload = () => {
      done()
      const headers = new Headers()
      for (const line of xhr.getAllResponseHeaders().split(/\r?\n/)) {
        const colon = line.indexOf(':')
        if (colon > 0) headers.append(line.slice(0, colon).trim(), line.slice(colon + 1).trim())
      }
      const empty = xhr.status === 204 || xhr.status === 205 || xhr.status === 304
      resolve(new Response(empty ? null : xhr.responseText, { status: xhr.status, headers }))
    }
    xhr.onerror = () => {
      done()
      reject(new TypeError('Network request failed'))
    }
    xhr.onabort = () => {
      done()
      reject(abortError())
    }
    xhr.send(body)
  })

/** `fetch`: no progress (the row shows an indeterminate bar), but seen by fetch stubs and the harness. */
export const fetchUploadTransport: UploadTransport = ({ url, method, body, signal }) =>
  fetch(url, {
    method,
    body,
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    signal: signal ?? null,
  })

let uploadTransport: UploadTransport =
  typeof XMLHttpRequest === 'undefined' ? fetchUploadTransport : xhrUploadTransport

/** Tests and the review harness answer uploads through their `fetch` stand-in. */
export function setUploadTransport(transport: UploadTransport): void {
  uploadTransport = transport
}

export interface UploadOptions {
  method?: 'POST' | 'PUT'
  query?: Record<string, QueryValue>
  signal?: AbortSignal | undefined
  /** 0–1 as the bytes go out; never called when the transport cannot tell. */
  onProgress?: ((fraction: number) => void) | undefined
}

/**
 * Sends one file as `multipart/form-data` in the field `file` (the API's upload shape): the same
 * session, problem documents and redirects as every other call.
 */
export async function apiUpload<T>(path: string, file: File, options: UploadOptions = {}): Promise<T> {
  const { method = 'POST', query, signal, onProgress } = options
  const body = new FormData()
  body.append('file', file, file.name)
  let res: Response
  try {
    res = await uploadTransport({
      url: buildUrl(path, query),
      method,
      body,
      signal,
      onProgress: onProgress
        ? (sent, total) => onProgress(total > 0 ? Math.min(1, sent / total) : 0)
        : undefined,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError({ status: 0, code: ClientErrorCode.NETWORK_ERROR })
  }
  if (!res.ok) {
    const error = await parseErrorResponse(res)
    if (error.status === 401) unauthorizedHandler?.()
    if (error.status === 403 && error.code === PASSWORD_CHANGE_REQUIRED) passwordChangeHandler?.()
    throw error
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') return undefined as T
  return (await res.json()) as T
}

export const api = {
  get: <T>(path: string, options?: BodylessOptions) => apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: BodylessOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: BodylessOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: BodylessOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: BodylessOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
  download: apiDownload,
  upload: apiUpload,
  stream: apiStream,
}
